/**
 * Icons als Inline-SVG statt Icon-Bibliothek: Eine Offline-App trägt jedes Kilobyte
 * mit sich herum, und gebraucht wird eine überschaubare, feste Zahl von Symbolen.
 *
 * Durchgehender Strichstil — 1,75 px, eckige Enden, keine Rundungen. Dasselbe Register
 * wie die Kanten der Oberfläche.
 *
 * Zwei Formen stehen in `icon-paths.ts`, weil die Einsprungpunkte am App-Symbol sie
 * ebenfalls brauchen — dort als PNG.
 */
import { ICON_RASTER, ICON_STRICH, PFAD_KATALOG, PFAD_SUCHE } from './icon-paths'

type IconProps = { className?: string }

const base = {
  viewBox: `0 0 ${ICON_RASTER} ${ICON_RASTER}`,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: ICON_STRICH,
  strokeLinecap: 'square' as const,
  strokeLinejoin: 'miter' as const,
  'aria-hidden': true,
}

export function IconHome({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M3 11 12 3l9 8" />
      <path d="M5.5 9.5V21h13V9.5" />
      <path d="M9.5 21v-6h5v6" />
    </svg>
  )
}

export function IconSearch({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      {PFAD_SUCHE.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}

export function IconCatalog({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      {PFAD_KATALOG.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}

export function IconSettings({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <path d="M16 4.5v5M10 14.5v5" />
    </svg>
  )
}

/** Favoritenstern — gefüllt, wenn gesetzt. Die Form allein trägt die Aussage. */
export function IconStar({ className = 'size-6', filled = false }: IconProps & { filled?: boolean }) {
  return (
    <svg {...base} className={className} fill={filled ? 'currentColor' : 'none'}>
      <path d="M12 3.5 14.6 9l6 .8-4.3 4.3 1 6L12 17.3 6.7 20.1l1-6L3.4 9.8 9.4 9z" />
    </svg>
  )
}

export function IconShare({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M12 16V4M8 7l4-4 4 4" />
      <path d="M5 12v8h14v-8" />
    </svg>
  )
}

export function IconBack({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M15 4 7 12l8 8" />
    </svg>
  )
}

export function IconArrowLeft({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M20 12H5M11 6l-6 6 6 6" />
    </svg>
  )
}

export function IconArrowRight({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M4 12h15M13 6l6 6-6 6" />
    </svg>
  )
}

export function IconClose({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M5 5 19 19M19 5 5 19" />
    </svg>
  )
}

export function IconFullscreen({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
    </svg>
  )
}

/** Kachelansicht: zwei Spalten, je Bild oben und Titel darunter. */
export function IconKacheln({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M4 5h6v14H4zM14 5h6v14h-6z" />
      <path d="M4 13h6M14 13h6" />
    </svg>
  )
}

/** Listenansicht: Vorschau links, Text rechts. */
export function IconListe({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M4 5h4v5H4zM4 14h4v5H4z" />
      <path d="M11 6.5h9M11 9h6M11 15.5h9M11 18h6" />
    </svg>
  )
}

/** Ziehgriff — die geriffelte Fläche, an der man einen Eintrag anfasst. */
export function IconGriff({ className = 'size-6' }: IconProps) {
  return (
    <svg {...base} className={className}>
      <path d="M7 8h10M7 12h10M7 16h10" />
    </svg>
  )
}
